import {
  HttpContextToken,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { finalize, Observable, of, shareReplay, tap } from 'rxjs';
import { SKIP_INTERCEPTOR } from '../services/skip-interceptor.token';

/**
 * Opt-in per-request TTL (ms) for caching a successful GET response. Default `0` = no
 * caching. Set on idempotent lookups that change rarely (e.g. UDC option lists) to avoid
 * re-fetching the same data repeatedly:
 *
 * ```ts
 * this.http.get(url, { context: new HttpContext().set(HTTP_CACHE_TTL, 60_000) });
 * ```
 */
export const HTTP_CACHE_TTL = new HttpContextToken<number>(() => 0);

interface CacheEntry {
  expires: number;
  response: HttpResponse<unknown>;
}

/** Concurrent identical GETs share one network call; cleared as each settles. */
const inFlight = new Map<string, Observable<HttpEvent<unknown>>>();
/** TTL-cached successful GET responses (opt-in via HTTP_CACHE_TTL). */
const cache = new Map<string, CacheEntry>();

/**
 * GET request de-duplication + opt-in short-TTL caching, to stop a data-heavy client from
 * flooding the backend with redundant reads (Phase P, "chatty client" guard).
 *
 * - **Dedup (always on):** two identical GETs issued while the first is still in flight
 *   share a single backend request — zero staleness risk, since they overlap in time.
 * - **TTL cache (opt-in):** when a caller sets {@link HTTP_CACHE_TTL} > 0, the successful
 *   response is served from memory until it expires.
 *
 * Non-GET requests pass straight through, so mutations are never cached or shared.
 */
export function httpCacheInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  if (req.method !== 'GET' || req.context.get(SKIP_INTERCEPTOR)) {
    return next(req);
  }

  // Tenant-scope the key: `territoryContextInterceptor` runs upstream of this one and sets
  // `X-Territory-Id`, so by the time the key is computed the request is already scoped to a
  // territory. Keying on the URL alone would let two identical URLs under two different
  // territory scopes collide — a cross-territory cache/dedup hit.
  const key = `${req.headers.get('X-Territory-Id') ?? ''}|${req.urlWithParams}`;
  const ttl = req.context.get(HTTP_CACHE_TTL);

  if (ttl > 0) {
    const hit = cache.get(key);
    if (hit && hit.expires > Date.now()) {
      return of(hit.response.clone());
    }
  }

  const existing = inFlight.get(key);
  if (existing) {
    return existing;
  }

  const shared = next(req).pipe(
    tap((event) => {
      if (ttl > 0 && event instanceof HttpResponse) {
        cache.set(key, { expires: Date.now() + ttl, response: event.clone() });
      }
    }),
    finalize(() => inFlight.delete(key)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  inFlight.set(key, shared);
  return shared;
}

/** Clears the TTL cache (e.g. on logout / territory switch). Dedup state self-clears. */
export function clearHttpCache(): void {
  cache.clear();
}
